/**
 * Account Query Hooks
 * Queries for fetching account status and configuration
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "../../useAuth";
import { apiGet } from "../../../lib/api-utils";
import { QUERY_CONFIG, ENDPOINTS, RETRY_CONFIG } from "../config";
import { queryKeys } from "../query-keys";
import type { AccountStatus } from "../types";
import { logDebug } from "../utils";

/**
 * Get Account Configuration Status
 * Fetches the current account status including connected calendars
 */
export const useAccountStatus = () => {
  const { getAuthToken } = useAuth();

  // Memoize the enabled condition to prevent infinite re-renders
  const hasToken = useMemo(() => !!getAuthToken(), [getAuthToken]);

  return useQuery({
    queryKey: queryKeys.account.status(),
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      logDebug("useAccountStatus API Call", {
        hasToken: !!token,
        tokenPrefix: token ? token.substring(0, 20) + "..." : "null",
        timestamp: new Date().toISOString(),
      });

      const response = await apiGet(ENDPOINTS.account.status, token);

      logDebug("useAccountStatus API Response", {
        response,
        timestamp: new Date().toISOString(),
      });

      return response as AccountStatus;
    },
    enabled: hasToken,
    staleTime: QUERY_CONFIG.accountStatus.staleTime,
    gcTime: QUERY_CONFIG.accountStatus.gcTime,
    retry: RETRY_CONFIG.standard,
  });
};
